import { IWorkroomMember, IWorkroomUserPermissions } from 'Frontend/scripts/models/workrooms.model'
import {
  addWorkroomAdmin,
  getWorkroomMembers,
  removeMembershipFromUser,
  removeWorkroomAdmin,
} from 'Frontend/scripts/services/workrooms.service'
import { FunctionComponent, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import Dialog from 'Features/Dialog/Dialog'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { convertDate } from 'Frontend/scripts/convertDates'
import { useTranslation } from 'react-i18next'

interface WorkroomMembersProps {
  id: number
  permissions: IWorkroomUserPermissions | undefined
}

const WorkroomMembers: FunctionComponent<WorkroomMembersProps> = ({ id, permissions }) => {
  const [members, setMembers] = useState<IWorkroomMember[]>([])
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [isDeleteAdminDialogOpen, setIsDeleteAdminDialogOpen] = useState<boolean>(false)
  const [selectedUserIdToDelete, setSelectedUserIdToDelete] = useState<number | null>(null)
  const [selectedAdminUserIdToDelete, setSelectedAdminUserIdToDelete] = useState<number | null>(null)
  const { t } = useTranslation()

  const removeMemberFromWorkroom = (userId: number) => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      removeMembershipFromUser(token.csrfToken, id, userId).then(() => {
        const membersWithoutRemovedMember = members.filter((member) => member.userId !== userId)
        setMembers(membersWithoutRemovedMember)
        setIsDeleteDialogOpen(false)
      })
    })
  }

  const makeMemberAdmin = (userId: number) => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      addWorkroomAdmin(token.csrfToken, id, userId).then(() => {
        getWorkroomMembers(id).then((members: IWorkroomMember[]) => {
          setMembers(members)
        })
      })
    })
  }
  const removeMemberAdmin = (userId: number) => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      removeWorkroomAdmin(token.csrfToken, id, userId).then(() => {
        getWorkroomMembers(id).then((members: IWorkroomMember[]) => {
          setMembers(members)
          setIsDeleteAdminDialogOpen(false)
        })
      })
    })
  }

  useEffect(() => {
    getWorkroomMembers(id).then((members: IWorkroomMember[]) => {
      setMembers(members)
    })
  }, [])
  return (
    <>
      <h2 className="livstecknet__section-headline-text">{t('members')}</h2>
      <div className="d-flex flex-column gap-2">
        {members.map((member) => (
          <div
            key={`member-${member.userId}`}
            className="flex-column livstecknet__listing-content-row workrooms__members-card"
          >
            <a href={member.viewProfileUrl} className="workrooms__members-card-heading">
              {member.lastname}&nbsp;
              {member.firstname}
            </a>

            {member.isWorkroomAdministrator ? (
              <span className="workrooms__members-card-role">
                {member.isWorkroomOwner ? t('admin-and-owner') : t('administrator')}
              </span>
            ) : member.isWorkroomOwner ? (
              <span className="workrooms__members-card-role">{t('owner')}</span>
            ) : (
              <span className="workrooms__members-card-role">{t('member')}</span>
            )}
            {member.mobilePhone && (
              <a href={`tel:${member.mobilePhone}`} className="workrooms__members-card-info">
                <span className="fa fa-mobile-phone me-2 workrooms__members-card-icon workrooms__members-card-icon--large"></span>{' '}
                {member.mobilePhone}
              </a>
            )}
            {member.phone && (
              <a href={`tel:${member.phone}`} className="workrooms__members-card-info">
                <span className="fa fa-phone me-2 workrooms__members-card-icon"></span> {member.phone}
              </a>
            )}
            {member.email && (
              <a href={`mailto:${member.email}`} className="workrooms__members-card-info">
                <span className="fa fa-envelope me-2 workrooms__members-card-icon"></span> {member.email}
              </a>
            )}
            {member.membershipDate && (
              <span className="workrooms__members-card-role mt-1 mb-0">
                {t('member-since')}: {convertDate(member.membershipDate)}
              </span>
            )}
            <div className="d-flex justify-content-end gap-2 mt-3">
              {member.isWorkroomAdministrator
                ? permissions?.removeWorkroomAdmin && (
                    <>
                      <Button
                        variant="dark"
                        onClick={() => {
                          setSelectedAdminUserIdToDelete(member.userId)
                          setIsDeleteAdminDialogOpen(true)
                        }}
                      >
                        {t('remove-admin')}
                      </Button>
                      <Dialog
                        isOpen={isDeleteAdminDialogOpen}
                        verificationText={t('verification-delete-workroom-member-as-admin')}
                        submitText={t('yes-delete-workroom-member-as-admin')}
                        abortText={t('abort')}
                        buttonVariant="dark"
                        callbackSubmit={() => {
                          if (selectedAdminUserIdToDelete !== null) {
                            removeMemberAdmin(selectedAdminUserIdToDelete)
                          }
                        }}
                        callbackClose={() => {
                          setIsDeleteAdminDialogOpen(false)
                          setSelectedAdminUserIdToDelete(null)
                        }}
                      />
                    </>
                  )
                : permissions?.addWorkroomAdmin && (
                    <Button variant="secondary" onClick={() => makeMemberAdmin(member.userId)}>
                      {t('make-admin')}
                    </Button>
                  )}
              {permissions?.removeWorkroomMembership && (
                <>
                  <Button
                    variant="dark"
                    className="flex-shrink-0"
                    onClick={() => {
                      setSelectedUserIdToDelete(member.userId)
                      setIsDeleteDialogOpen(true)
                    }}
                  >
                    {t('remove-member')}
                  </Button>
                  <Dialog
                    isOpen={isDeleteDialogOpen}
                    verificationText={t('verification-delete-workroom-member')}
                    submitText={t('yes-delete-workroom-member')}
                    abortText={t('abort')}
                    buttonVariant="dark"
                    callbackSubmit={() => {
                      if (selectedUserIdToDelete !== null) {
                        removeMemberFromWorkroom(selectedUserIdToDelete)
                      }
                    }}
                    callbackClose={() => {
                      setIsDeleteDialogOpen(false)
                      setSelectedUserIdToDelete(null)
                    }}
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
export default WorkroomMembers
